import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, UserPlus, Heart, MessageCircle, Repeat2, AtSign, Mail, CornerDownRight, Loader2, Share2, Radio } from "lucide-react";
import { AuraRing } from "@/components/brand/AuraRing";
import { VerifiedBadge } from "@/components/brand/VerifiedBadge";
import { usePoll } from "@/hooks/usePoll";
import { api, mediaUrl, timeAgo } from "@/lib/api";
import { cn } from "@/lib/utils";

const META = {
  follow: { icon: UserPlus, verb: "followed you", color: "text-aura-cyan" },
  follow_back: { icon: UserPlus, verb: "followed you back", color: "text-aura-cyan" },
  like: { icon: Heart, verb: "liked your post", color: "text-red-500" },
  comment: { icon: MessageCircle, verb: "commented on your post", color: "text-white" },
  reply: { icon: CornerDownRight, verb: "replied to your comment", color: "text-white" },
  comment_like: { icon: Heart, verb: "liked your comment", color: "text-red-500" },
  mention: { icon: AtSign, verb: "mentioned you", color: "text-aura-cyan" },
  repost: { icon: Repeat2, verb: "reposted your post", color: "text-aura-mint" },
  message: { icon: Mail, verb: "sent you a message", color: "text-white" },
  story_share: { icon: Share2, verb: "shared your post to their story", color: "text-aura-gold" },
  live_started: { icon: Radio, verb: "went live on the Founders Channel", color: "text-red-400" },
  live_reminder: { icon: Radio, verb: "Founders LIVE starts in 15 minutes", color: "text-aura-gold" },
};

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    const list = await api.notifications();
    setItems(list);
    api.notificationsReadAll().catch(() => {});
    window.dispatchEvent(new Event("notif-read"));
  }, []);

  usePoll(load, 5000, []);

  const route = (n) => {
    if (n.type === "message") return navigate("/messages");
    if (n.type === "live_started" || n.type === "live_reminder") return navigate(n.event_id ? `/live/events/${n.event_id}` : "/live/founders");
    if (["follow", "follow_back", "mention"].includes(n.type)) return navigate(`/u/${n.actor.username}`);
    if (n.post_id) return navigate(`/post/${n.post_id}${n.comment_id ? `?c=${n.comment_id}` : ""}`);
    navigate("/u/me");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 md:px-6">
      <h1 className="mb-4 font-display text-2xl font-bold text-white">Notifications</h1>
      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="mb-5 grid h-16 w-16 place-items-center rounded-2xl border border-am-border bg-am-surface2"><Bell className="h-7 w-7 text-am-text3" /></div>
          <p className="text-sm font-semibold text-white">No notifications yet.</p>
          <p className="mt-1 max-w-xs text-xs text-am-text2">When people follow you, like your posts, comment or mention you, you'll see it here.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {items.map((n) => {
            const m = META[n.type] || META.like;
            const Icon = m.icon;
            return (
              <button key={n.id} onClick={() => route(n)} data-testid={`notif-${n.type}`} className={cn("flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors hover:bg-white/5", !n.read && "bg-am-surface/60")}>
                <span className="relative">
                  <AuraRing src={mediaUrl(n.actor?.picture) || undefined} size={42} ring={2} fallback={n.actor?.name} />
                  <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border-2 border-am-bg bg-am-surface3"><Icon className={cn("h-3 w-3", m.color)} /></span>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-am-text">
                    <span className="inline-flex items-center gap-1 font-semibold text-white">{n.actor?.name}{n.actor?.is_verified && <VerifiedBadge size={12} />}</span>{" "}
                    <span className="text-am-text2">{m.verb}</span>
                  </p>
                  <span className="text-xs text-am-text3">{timeAgo(n.created_at)}</span>
                </div>
                {!n.read && <span className="h-2 w-2 shrink-0 rounded-full aura-radial" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
